import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import YappyliLogo from './YappyliLogo';

// Desktop: same three links as the design; they jump to homepage sections from any page
const LINKS = [
  { to: '/#features', label: 'Features' },
  { to: '/#modes', label: 'Dark Mode' },
  { to: '/#download', label: 'Download' },
  { to: '/business', label: 'Business' },
];

// Mobile menu: the section links plus the other pages
const MOBILE_LINKS = [
  { to: '/#features', label: 'Features', icon: 'fa-solid fa-layer-group' },
  { to: '/#modes', label: 'Dark Mode', icon: 'fa-solid fa-moon' },
  { to: '/download', label: 'Download', icon: 'fa-solid fa-download' },
  { to: '/business', label: 'Business', icon: 'fa-solid fa-store' },
  { to: '/about', label: 'About', icon: 'fa-solid fa-circle-info' },
  { to: '/privacy', label: 'Privacy', icon: 'fa-solid fa-shield-halved' },
  { to: '/contact', label: 'Contact', icon: 'fa-solid fa-envelope' },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();

  const isActive = (to) => {
    const [path, h] = to.split('#');
    return h ? pathname === path && hash === `#${h}` : pathname === path;
  };

  // Close the menu whenever the page or section changes
  useEffect(() => setOpen(false), [pathname, hash]);

  // Close on Escape, and stop the page scrolling behind the open menu
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    // Close if the screen is widened to desktop size
    const mq = window.matchMedia('(min-width: 769px)');
    const onWide = (e) => e.matches && setOpen(false);
    mq.addEventListener?.('change', onWide);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      mq.removeEventListener?.('change', onWide);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <nav className={`yp navbar ${open ? 'menu-open' : ''}`}>
      <div className="container nav-container">
        <Link to="/" className="logo" onClick={() => setOpen(false)}>
          <YappyliLogo />
        </Link>

        <div className="nav-links">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={isActive(l.to) ? 'active' : undefined}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="nav-actions">
          <Link to="/download" className="btn btn-primary nav-cta">
            Get App
          </Link>
          <button
            type="button"
            className="menu-toggle"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            <i className={`fa-solid ${open ? 'fa-xmark' : 'fa-bars'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <div id="mobile-menu" className="mobile-menu" hidden={!open}>
        <div className="container">
          {MOBILE_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={`mobile-link ${isActive(l.to) ? 'active' : ''}`}>
              <span className="mobile-link-icon"><i className={l.icon}></i></span>
              {l.label}
              <i className="fa-solid fa-chevron-right mobile-link-arrow"></i>
            </Link>
          ))}
          <Link to="/download" className="btn btn-primary mobile-menu-cta">
            <i className="fa-solid fa-mobile-screen"></i> Get the App
          </Link>
        </div>
      </div>
      {open && <div className="mobile-backdrop" onClick={() => setOpen(false)} aria-hidden="true"></div>}
    </nav>
  );
}
